import {
  canAcceptApplication,
  canCancelApplication,
  canSignAuthenticAct,
  canSignPromiseOfSale,
  canUnacceptApplication,
  getApplicationEnrichedStatus,
} from '@almaris/shared/helpers/getApplicationInfo'

import { useUpdateApplicationMutation, WebappApplicationFragment } from '@gql'
import React, { useCallback, useState } from 'react'
import { trpc } from 'src/trpc'
import { useTranslation } from 'react-i18next'
import { toast } from '@/components/ui/use-toast'
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog'
import { Button } from '@/components/ui/button'
import { PromiseOfSaleForm } from './PromiseOfSaleForm'
import { AuthenticActForm } from './AuthenticActForm'
import { FileUploadDialog, OnUploadProps } from '@/file/FileUploadDialog'

type ApplicationAcceptationActionsProps = {
  application: WebappApplicationFragment
  isAdmin: boolean
  someApplicationIsAccepted: boolean
}

export const ApplicationAcceptationActions = ({
  application,
  isAdmin,
  someApplicationIsAccepted,
}: ApplicationAcceptationActionsProps) => {
  const { t } = useTranslation()
  const [isAcceptModalOpen, setOpenAcceptModal] = useState(false)
  const [isUnacceptModalOpen, setOpenUnacceptModal] = useState(false)
  const [isCancelModalOpen, setOpenCancelModal] = useState(false)

  const applicationEnrichedStatus = getApplicationEnrichedStatus(application)
  const canAcceptOffer = canAcceptApplication(
    isAdmin,
    someApplicationIsAccepted,
    application
  )
  const canCancelOffer = canCancelApplication(isAdmin, application)
  const canUnacceptOffer = canUnacceptApplication(isAdmin, application)
  const canModifyPromiseOfSale = canSignPromiseOfSale(isAdmin, application)
  const canModifyAuthenticAct = canSignAuthenticAct(isAdmin, application)

  const unacceptButtonText =
    applicationEnrichedStatus === 'UnderPromise'
      ? t('ApplicationSheet.unacceptOffer.buttonUnderPromise')
      : t('ApplicationSheet.unacceptOffer.button')

  const [updateApplication] = useUpdateApplicationMutation({
    refetchQueries: ['getApplicationsStatusesByEstatesIds'],
  })

  const acceptOffer = useCallback(async () => {
    if (!canAcceptOffer) return

    try {
      await trpc.application.acceptApplication.mutate({
        applicationId: application.id,
      })
      toast({
        title: t('ApplicationSheet.acceptOfferCall.success.title'),
        description: t('ApplicationSheet.acceptOfferCall.success.description'),
        variant: 'success',
      })
    } catch (error) {
      console.error(error)
      toast({
        title: t('ApplicationSheet.acceptOfferCall.error.title'),
        description: t('ApplicationSheet.acceptOfferCall.error.description'),
        variant: 'destructive',
      })
    }
  }, [application, canAcceptOffer, t])

  const unacceptOffer = useCallback(async () => {
    if (!canUnacceptOffer) return

    try {
      await trpc.application.unacceptApplication.mutate({
        applicationId: application.id,
      })
      toast({
        title: t('ApplicationSheet.unacceptOffer.success.title'),
        description: t('ApplicationSheet.unacceptOffer.success.description'),
        variant: 'success',
      })
    } catch (error) {
      console.error(error)
      toast({
        title: t('ApplicationSheet.unacceptOffer.error.title'),
        description: t('ApplicationSheet.unacceptOffer.error.description'),
        variant: 'destructive',
      })
    }
  }, [application, canUnacceptOffer, t])

  const cancelOfferAfterUpload = useCallback(
    async ({ id }: OnUploadProps) => {
      if (!canCancelOffer || id == null) {
        toast({
          title: t('ApplicationSheet.cancelOffer.error.title'),
          description: t('ApplicationSheet.cancelOffer.error.description'),
          variant: 'destructive',
        })
        return
      }

      try {
        // Set file id in the application via graphQL
        await updateApplication({
          variables: {
            id: application.id,
            values: {
              cancellationDocumentFileId: id,
            },
          },
        })

        // Cancel the application
        await trpc.application.cancelApplication.mutate({
          applicationId: application.id,
        })
        toast({
          title: t('ApplicationSheet.cancelOffer.success.title'),
          description: t('ApplicationSheet.cancelOffer.success.description'),
          variant: 'success',
        })
      } catch (error) {
        console.error(error)
        toast({
          title: t('ApplicationSheet.cancelOffer.error.title'),
          description: t('ApplicationSheet.cancelOffer.error.description'),
          variant: 'destructive',
        })
      } finally {
        setOpenCancelModal(false)
      }
    },
    [application, canCancelOffer, t, updateApplication]
  )

  return (
    <div className="tw-space-y-6">
      {canCancelOffer && (
        <div>
          <Button type="button" onClick={() => setOpenCancelModal(true)}>
            {t('ApplicationSheet.cancelOffer.button')}
          </Button>
          <FileUploadDialog
            description={t('ApplicationSheet.cancelOffer.description')}
            onUpload={cancelOfferAfterUpload}
            canSetCustomName={false}
            open={isCancelModalOpen}
            onOpenChange={() => setOpenCancelModal(false)}
          ></FileUploadDialog>
        </div>
      )}
      {canAcceptOffer && (
        <div>
          <Button type="button" onClick={() => setOpenAcceptModal(true)}>
            {t('ApplicationSheet.sendMail.button')}
          </Button>
          <ConfirmationDialog
            open={isAcceptModalOpen}
            onOpenChange={setOpenAcceptModal}
            onConfirm={acceptOffer}
            description={t('ApplicationSheet.sendMail.description')}
          />
        </div>
      )}
      {canUnacceptOffer && (
        <div>
          <Button type="button" onClick={() => setOpenUnacceptModal(true)}>
            {unacceptButtonText}
          </Button>
          <ConfirmationDialog
            open={isUnacceptModalOpen}
            onOpenChange={setOpenUnacceptModal}
            onConfirm={unacceptOffer}
            description={t('ApplicationSheet.unacceptOffer.description')}
          />
        </div>
      )}
      {canModifyPromiseOfSale && (
        <PromiseOfSaleForm application={application} />
      )}
      {canModifyAuthenticAct && <AuthenticActForm application={application} />}
    </div>
  )
}
